"use client";

import Image from "next/image";
import React from "react";
import Slider from "react-slick";

import Section from "../Section";
const styles = new Proxy({"Title":"Title-36b8b","Partners":"Partners-36b8b","SliderItem":"SliderItem-36b8b","SliderItemFlex":"SliderItemFlex-36b8b"}, { get: (m, k) => (typeof k === "string" ? (m[k] ?? k) : m[k]) });
/*import "./PartnerSlider.scss";*/

export type SliderItem = {
  image: { url: string };
  altTxt: string;
  width: number;
  height: number;
  sizes: string;
  customContent?: (imgElement: React.JSX.Element) => React.ReactNode;
};

interface PartnersClientProps {
  title?: string;
  sliderItems: SliderItem[];
}

const partner1 = "/images/IndexPage/Partners/partner-otp.png";
const partner2 = "/images/IndexPage/Partners/partner-konica.png";
const partner3 = "/images/IndexPage/Partners/partner-decathlon.png";
const partner4 = "/images/IndexPage/Partners/partner-dsk.png";
const partner5 = "/images/IndexPage/Partners/partner-cardiff.png";
const partner6 = "/images/IndexPage/Partners/partner-mokk.png";
const partner7 = "/images/IndexPage/Partners/partner-prezi.png";
const partner8 = "/images/IndexPage/Partners/partner-sanofi.png";
const partner9 = "/images/IndexPage/Partners/partner-hungaro.png";
const partner10 = "/images/IndexPage/Partners/partner-natwest.png";
const partner11 = "/images/IndexPage/Partners/partner-catapult.png";
const partner12 = "/images/IndexPage/Partners/partner-wiseguys.png";
const partner13 = "/images/IndexPage/Partners/partner-erlang.png";
const partner14 = "/images/IndexPage/Partners/partner-neurology.png";
const partner15 = "/images/IndexPage/Partners/partner-microsoft.png";

const DEFAULT_PARTNERS: SliderItem[] = [
  {
    image: { url: partner1 },
    altTxt: "OTP Bank logo",
    width: 2979,
    height: 627,
    sizes: "140px",
  },
  {
    image: { url: partner4 },
    altTxt: "DSK logo",
    width: 839,
    height: 194,
    sizes: "140px",
  },
  {
    image: { url: partner3 },
    altTxt: "Decathlon logo",
    width: 3764,
    height: 936,
    sizes: "140px",
  },
  {
    image: { url: partner2 },
    altTxt: "Konica Minolta logo",
    width: 4817,
    height: 650,
    sizes: "140px",
  },
  {
    image: { url: partner6 },
    altTxt: "Association of Notaries logo",
    width: 945,
    height: 244,
    sizes: "160px",
    customContent: (imgElement) => (
      <figure className="mb-0 text-center">
        {imgElement}
        <figcaption className="mt-2">Association of Notaries</figcaption>
      </figure>
    ),
  },
  {
    image: { url: partner8 },
    altTxt: "Sanofi logo",
    width: 3301,
    height: 704,
    sizes: "160px",
    customContent: (imgElement) => (
      <div className={styles.SliderItem}>{imgElement}</div>
    ),
  },
  {
    image: { url: partner5 },
    altTxt: "Cardiff logo",
    width: 2033,
    height: 816,
    sizes: "140px",
  },
  {
    image: { url: partner7 },
    altTxt: "Prezi logo",
    width: 863,
    height: 316,
    sizes: "140px",
  },
  {
    image: { url: partner9 },
    altTxt: "HungaroControl logo",
    width: 600,
    height: 72,
    sizes: "140px",
  },
  {
    image: { url: partner14 },
    altTxt: "National Institute of Mental Health logo",
    width: 1020,
    height: 326,
    sizes: "180px",
    customContent: (imgElement) => (
      <div className="p-0">
        <div className="my-0">
          {imgElement}{" "}
          <p className="my-0 d-none d-md-block">
            National Institue of Mental Health,
            <br />
            Neurology and Neurosurgery
          </p>
        </div>
      </div>
    ),
  },
  {
    image: { url: partner10 },
    altTxt: "NatWest logo",
    width: 1322,
    height: 213,
    sizes: "140px",
    customContent: (imgElement) => (
      <figure className="text-center mb-0">
        {imgElement}
        <figcaption className="mt-3 mb-0">Fintech Accelerator</figcaption>
      </figure>
    ),
  },
  {
    image: { url: partner12 },
    altTxt: "Startup Wise Guys logo",
    width: 360,
    height: 119,
    sizes: "140px",
  },
  {
    image: { url: partner15 },
    altTxt: "Microsoft logo",
    width: 554,
    height: 117,
    sizes: "140px",
  },
  {
    image: { url: partner11 },
    altTxt: "Digital Catapult logo",
    width: 600,
    height: 232,
    sizes: "140px",
  },
  {
    image: { url: partner13 },
    altTxt: "Erlang Solutions logo",
    width: 565,
    height: 61,
    sizes: "140px",
  },
];

const SETTINGS = {
  speed: 6000,
  autoplay: true,
  autoplaySpeed: 0,
  cssEase: "linear",
  slidesToShow: 1,
  slidesToScroll: 1,
  variableWidth: true,
  arrows: false,
};

const getSliderItems = (items: SliderItem[]) => {
  const duplicateCount = items.length < 5 ? 5 : items.length < 10 ? 3 : 1;
  return Array(duplicateCount).fill(items).flat();
};

const PartnersClient: React.FC<PartnersClientProps> = ({
  title,
  sliderItems,
}) => {
  const usedItems = sliderItems?.length ? sliderItems : DEFAULT_PARTNERS;

  return (
    <Section className={styles.Partners}>
      <h2 className={styles.Title}>{title || "Our partners"}</h2>
      <div className="row mt-5">
        <div className="col-12">
          <Slider {...SETTINGS} className="PartnerSlider">
            {getSliderItems(usedItems).map((partner, index) => {
              const imgElement = (
                <Image
                  src={partner.image.url}
                  alt={partner.altTxt}
                  className="img-fluid"
                  sizes={partner.sizes}
                  width={partner.width}
                  height={partner.height}
                />
              );

              return (
                <div key={index} className={styles.SliderItem}>
                  <div>
                    {partner.customContent
                      ? partner.customContent(imgElement)
                      : imgElement}
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </Section>
  );
};

export default PartnersClient;
